import { createContext, ReactElement, useCallback, useContext, useMemo, useRef, useState } from 'react';

import { TextEditorValue } from '@dreryk/react-text-editor';

import { VisitFormController } from '../../visit/form/visitForm.types';

import { VisitField, VisitFormContextType } from './VisitFormContext.types';

const VisitFormContext = createContext<VisitFormContextType | null>(null);

type RedactionContextType = {
   lockedField: VisitField | null;
   setLockedField: (field: VisitField | null) => void;
};

const RedactionContext = createContext<RedactionContextType>({
   lockedField: null,
   setLockedField: () => undefined,
});

export const useVisitFormContext = (): VisitFormContextType => {
   const context = useContext(VisitFormContext);
   if (context === null) {
      throw new Error('useVisitFormContext must be used within VisitFormContextProvider!');
   }
   return context;
};

export const useRedactionContext = (): RedactionContextType => useContext(RedactionContext);

type VisitFormContextProviderProps = {
   readonly children: ReactElement | ReactElement[];
};

const MAX_FORMATTED_LENGTH = 10000;

/**
 * Wraps plain text as a single appended paragraph in the editor's HTML format.
 * Used only by appendToField — the one operation that writes new content.
 */
function buildAppendedValue(current: TextEditorValue | null, text: string): TextEditorValue {
   // eslint-disable-next-line require-unicode-regexp
   const escaped = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
   const currentHtml = current?.getFormattedValue() ?? '';
   const combined = currentHtml.length > 0 ? `${currentHtml}<p>${escaped}</p>` : `<p>${escaped}</p>`;
   return TextEditorValue.fromFormatted(combined.slice(0, MAX_FORMATTED_LENGTH));
}

function getHtml(controller: VisitFormController, field: VisitField): string {
   if (field === 'INTERVIEW') {
      return controller.getValue('interview')?.getFormattedValue() ?? '';
   }
   if (field === 'EXAMINATION') {
      return controller.getValue('subjectiveAssessments')?.getFormattedValue() ?? '';
   }
   if (field === 'RECOMMENDATIONS') {
      return controller.getValue('recommendations')?.getFormattedValue() ?? '';
   }
   return '';
}

function setHtml(controller: VisitFormController, field: VisitField, html: string): void {
   const value = TextEditorValue.fromFormatted(html.slice(0, MAX_FORMATTED_LENGTH));
   if (field === 'INTERVIEW') {
      controller.setValue('interview', value, { shouldDirty: true, shouldValidate: true });
   } else if (field === 'EXAMINATION') {
      controller.setValue('subjectiveAssessments', value, { shouldDirty: true, shouldValidate: true });
   } else if (field === 'RECOMMENDATIONS') {
      controller.setValue('recommendations', value, { shouldDirty: true, shouldValidate: true });
   }
}

// eslint-disable-next-line max-lines-per-function
export const VisitFormContextProvider = (props: VisitFormContextProviderProps): ReactElement => {
   /*
    * Ref instead of state — avoids re-rendering the entire visit form subtree
    * when the controller is registered on mount.
    */
   const controllerRef = useRef<VisitFormController | null>(null);
   const [lockedField, setLockedField] = useState<VisitField | null>(null);

   const registerController = useCallback((controller: VisitFormController | null) => {
      controllerRef.current = controller;
   }, []);

   const appendToField = useCallback((field: VisitField, text: string) => {
      const controller = controllerRef.current;
      if (!controller) {
         return;
      }

      const currentHtml = getHtml(controller, field);
      const currentValue = currentHtml.length > 0 ? TextEditorValue.fromFormatted(currentHtml) : null;
      setHtml(controller, field, buildAppendedValue(currentValue, text).getFormattedValue());
   }, []);

   const getFieldHtml = useCallback((field: VisitField): string => {
      const controller = controllerRef.current;
      return controller ? getHtml(controller, field) : '';
   }, []);

   const setFieldHtml = useCallback((field: VisitField, html: string) => {
      const controller = controllerRef.current;
      if (controller) {
         setHtml(controller, field, html);
      }
   }, []);

   const contextValue = useMemo(
      () => ({
         appendToField: appendToField,
         getFieldHtml: getFieldHtml,
         setFieldHtml: setFieldHtml,
         registerController: registerController,
         getController: () => controllerRef.current,
      }),
      [appendToField, getFieldHtml, setFieldHtml, registerController]
   );

   const redactionContextValue = useMemo(() => ({ lockedField: lockedField, setLockedField: setLockedField }), [
      lockedField,
   ]);

   return (
      <VisitFormContext.Provider value={contextValue}>
         <RedactionContext.Provider value={redactionContextValue}>{props.children}</RedactionContext.Provider>
      </VisitFormContext.Provider>
   );
};

/*
 * Separate hook for VisitForm to register its controller.
 */
export const useVisitFormControllerRegistration = (): ((controller: VisitFormController | null) => void) => {
   const context = useContext(VisitFormContext) as
      | (VisitFormContextType & { registerController?: (controller: VisitFormController | null) => void })
      | null;

   if (context === null || !context.registerController) {
      return () => undefined;
   }
   return context.registerController;
};

/*
 * Hook to access the registered visit form controller.
 * Returns a getter function — call it inside event handlers, not during render.
 */
export const useGetVisitFormController = (): (() => VisitFormController | null) => {
   const context = useContext(VisitFormContext) as
      | (VisitFormContextType & { getController?: () => VisitFormController | null })
      | null;

   if (context === null || !context.getController) {
      return () => null;
   }
   return context.getController;
};
